"use client";

import { c as _c } from "react/compiler-runtime";
import { ArrowRight, Check, Code, Copy, Gem, List, Sparkles, Zap } from "lucide-react";
import Link from "next/link";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { getModelFamilyIcon } from "@llmgateway/shared/components";
import { formatCompactNumber } from "@llmgateway/shared/number-format";
import type { CodingModelCard } from "@/lib/coding-models";
export type CodingModelsView = "recommended" | "standard" | "premium" | "all";
interface CodingModelsShowcaseProps {
  models: CodingModelCard[];
  showCTA?: boolean;
  className?: string;
  showTabs?: boolean;
  defaultView?: CodingModelsView;
}
function formatContextSize(size: number): string {
  return formatCompactNumber(size);
}
function formatPrice(price: number): string {
  const perMillion = price * 1e6;
  if (perMillion < 0.01) {
    return perMillion.toFixed(4);
  }
  if (perMillion < 1) {
    return perMillion.toFixed(2);
  }
  return perMillion.toFixed(2);
}
const TAB_DEFINITIONS: {
  value: CodingModelsView;
  label: string;
  icon: React.ComponentType<{
    className?: string;
  }>;
  description: string;
}[] = [{
  value: "recommended",
  label: "Recommended",
  icon: Sparkles,
  description: "The latest open-weight lab models — the best value for coding agents."
}, {
  value: "standard",
  label: "Standard",
  icon: Zap,
  description: "No weekly cap — use them as far as your plan credits go."
}, {
  value: "premium",
  label: "Premium",
  icon: Gem,
  description: "Flagship models covered by the weekly premium fair-use allowance."
}, {
  value: "all",
  label: "All",
  icon: List,
  description: "Every coding-capable model on LLM Gateway."
}];
export function CodingModelsShowcase(t0) {
  const $ = _c(44);
  const {
    models,
    showCTA,
    className,
    showTabs: t1,
    defaultView: t2
  } = t0;
  const showTabs = t1 === undefined ? false : t1;
  const defaultView = t2 === undefined ? "recommended" : t2;
  const [copiedModel, setCopiedModel] = useState(null);
  const [view, setView] = useState(defaultView);
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  if ($[0] !== className || $[1] !== copiedModel || $[2] !== models || $[3] !== showTabs || $[4] !== view) {
    let t9;
    if ($[11] !== view) {
      t9 = model => {
        if (view === "recommended") {
          return model.recommended;
        }
        if (view === "standard") {
          return !model.premium;
        }
        if (view === "premium") {
          return model.premium;
        }
        return true;
      };
      $[11] = view;
      $[12] = t9;
    } else {
      t9 = $[12];
    }
    const visibleModels = models.filter(t9);
    let t10;
    if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
      t10 = async modelId => {
        await navigator.clipboard.writeText(modelId);
        setCopiedModel(modelId);
        setTimeout(() => setCopiedModel(null), 2000);
      };
      $[13] = t10;
    } else {
      t10 = $[13];
    }
    const copyToClipboard = t10;
    let t11;
    if ($[14] !== view) {
      t11 = TAB_DEFINITIONS.find(t => t.value === view);
      $[14] = view;
      $[15] = t11;
    } else {
      t11 = $[15];
    }
    const activeTab = t11;
    t5 = `rounded-lg border p-6 ${className ?? ""}`;
    let t12;
    if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
      t12 = <Code className="h-5 w-5" aria-hidden="true" />;
      $[16] = t12;
    } else {
      t12 = $[16];
    }
    const t13 = showTabs ? "Coding models" : "Recommended for coding agents";
    let t14;
    if ($[17] !== t13) {
      t14 = <div className="flex items-center gap-2">{t12}<p className="font-semibold">{t13}</p></div>;
      $[17] = t13;
      $[18] = t14;
    } else {
      t14 = $[18];
    }
    let t15;
    if ($[19] !== showTabs || $[20] !== view) {
      t15 = showTabs && <div className="inline-flex items-center gap-1 rounded-lg border bg-muted/30 p-1" role="tablist" aria-label="Filter coding models">{TAB_DEFINITIONS.map(tab => {
          const TabIcon = tab.icon;
          const active = view === tab.value;
          return <button key={tab.value} type="button" role="tab" aria-selected={active} onClick={() => setView(tab.value)} className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${active ? "bg-background text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"}`}><TabIcon className="h-3.5 w-3.5" />{tab.label}</button>;
        })}</div>;
      $[19] = showTabs;
      $[20] = view;
      $[21] = t15;
    } else {
      t15 = $[21];
    }
    if ($[22] !== t14 || $[23] !== t15) {
      t6 = <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">{t14}{t15}</div>;
      $[22] = t14;
      $[23] = t15;
      $[24] = t6;
    } else {
      t6 = $[24];
    }
    const t16 = showTabs && activeTab ? activeTab.description : "The latest open-weight-lab models \u2014 high performance on coding tasks with tool support and prompt caching.";
    if ($[25] !== t16) {
      t7 = <p className="text-sm text-muted-foreground mb-4">{t16}</p>;
      $[25] = t16;
      $[26] = t7;
    } else {
      t7 = $[26];
    }
    t8 = visibleModels.length === 0 && <p role="status" className="rounded-lg border border-dashed p-8 text-center text-sm text-muted-foreground">{models.length === 0 ? "Models are currently unavailable. Please try again shortly." : "No models in this category. Browse the full directory below."}</p>;
    t3 = "grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4";
    let t17;
    if ($[27] !== copiedModel) {
      t17 = model_0 => {
        const FamilyIcon = getModelFamilyIcon(model_0.family);
        return <div key={model_0.id} className="group relative flex flex-col gap-2 rounded-lg border p-4 transition-all hover:border-primary/50 hover:shadow-sm">{model_0.premium ? <span className="absolute right-3 top-3 inline-flex items-center gap-1 rounded-full border border-amber-500/30 bg-amber-500/10 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-amber-600 dark:text-amber-400"><Gem className="h-2.5 w-2.5" />Premium</span> : null}<div className="flex items-start justify-between gap-2"><div className="flex-1 min-w-0"><div className="flex items-center gap-2"><div className="flex h-6 w-6 shrink-0 items-center justify-center rounded bg-muted"><FamilyIcon className="h-4 w-4" /></div><span className="font-medium text-sm truncate">{model_0.name}</span></div></div></div><div className="flex items-center gap-2 mt-1"><code className="flex-1 text-xs bg-muted px-2 py-1 rounded font-mono truncate">{model_0.id}</code><button type="button" onClick={() => copyToClipboard(model_0.id)} className="shrink-0 p-1 rounded hover:bg-muted transition-[background-color,color,transform] duration-150 ease-out active:scale-[0.98]" title="Copy model ID"><span className="relative flex h-3 w-3 items-center justify-center"><Copy className={`h-3 w-3 text-muted-foreground transition-[opacity,transform] duration-150 ease-out motion-reduce:transition-[opacity] ${copiedModel === model_0.id ? "scale-[0.45] opacity-0" : "scale-100 opacity-100"}`} /><Check className={`absolute h-3 w-3 text-green-600 transition-[opacity,transform] duration-150 ease-out motion-reduce:transition-[opacity] ${copiedModel === model_0.id ? "scale-100 opacity-100" : "scale-[0.45] opacity-0"}`} /></span></button></div>{model_0.discount > 0 && <span className="w-fit rounded-md border px-2 py-0.5 text-xs font-medium bg-green-500/10 text-green-700 dark:text-green-400 border-green-500/20">{Math.round(model_0.discount * 100)}% off</span>}{(model_0.contextSize !== null || model_0.inputPrice !== null || model_0.outputPrice !== null) && <div className="text-xs text-muted-foreground space-y-1 mt-1">{model_0.contextSize !== null && <p>Context:{" "}<span className="font-mono font-medium text-foreground">{formatContextSize(model_0.contextSize)}</span></p>}{(model_0.inputPrice !== null || model_0.outputPrice !== null) && <p><span className="text-muted-foreground mr-1">starting from</span>{model_0.inputPrice !== null && <><span className="font-mono font-medium text-foreground">${formatPrice(model_0.inputPrice)}</span><span className="text-muted-foreground"> in</span></>}{model_0.outputPrice !== null && <><span className="text-muted-foreground mx-1">/</span><span className="font-mono font-medium text-foreground">${formatPrice(model_0.outputPrice)}</span><span className="text-muted-foreground"> out</span></>}<span className="text-muted-foreground/70 text-[10px] ml-1">/M tokens</span></p>}</div>}</div>;
      };
      $[27] = copiedModel;
      $[28] = t17;
    } else {
      t17 = $[28];
    }
    t4 = visibleModels.map(t17);
    $[0] = className;
    $[1] = copiedModel;
    $[2] = models;
    $[3] = showTabs;
    $[4] = view;
    $[5] = t3;
    $[6] = t4;
    $[7] = t5;
    $[8] = t6;
    $[9] = t7;
    $[10] = t8;
  } else {
    t3 = $[5];
    t4 = $[6];
    t5 = $[7];
    t6 = $[8];
    t7 = $[9];
    t8 = $[10];
  }
  let t9;
  if ($[29] !== t3 || $[30] !== t4) {
    t9 = <div className={t3}>{t4}</div>;
    $[29] = t3;
    $[30] = t4;
    $[31] = t9;
  } else {
    t9 = $[31];
  }
  let t10;
  if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <Link href="/models" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors">Browse the full directory<ArrowRight className="h-3 w-3" /></Link>;
    $[32] = t10;
  } else {
    t10 = $[32];
  }
  let t11;
  if ($[33] !== showCTA) {
    t11 = showCTA && <Button asChild={true}><Link href="/signup">Get Started</Link></Button>;
    $[33] = showCTA;
    $[34] = t11;
  } else {
    t11 = $[34];
  }
  let t12;
  if ($[35] !== t11) {
    t12 = <div className="mt-4 flex items-center justify-between">{t10}{t11}</div>;
    $[35] = t11;
    $[36] = t12;
  } else {
    t12 = $[36];
  }
  let t13;
  if ($[37] !== t12 || $[38] !== t5 || $[39] !== t6 || $[40] !== t7 || $[41] !== t8 || $[42] !== t9) {
    t13 = <div className={t5}>{t6}{t7}{t8}{t9}{t12}</div>;
    $[37] = t12;
    $[38] = t5;
    $[39] = t6;
    $[40] = t7;
    $[41] = t8;
    $[42] = t9;
    $[43] = t13;
  } else {
    t13 = $[43];
  }
  return t13;
}